import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { PageHeader, BadgePill, EmptyState } from '@/components/campus';
import {
  Users,
  DollarSign,
  Calendar,
  FileText,
  UserPlus,
  Wallet,
  TrendingUp,
  TrendingDown,
  Minus,
  Loader2,
  Trophy,
  Award,
  Medal,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== STAT CARD COMPONENT ====================
const StatCard = ({ stat, icon: Icon }) => {
  const { value, label, trend, suffix, prefix } = stat;
  
  const TrendIcon = trend.direction === 'up' ? TrendingUp 
    : trend.direction === 'down' ? TrendingDown 
    : Minus;
  
  const trendColor = trend.direction === 'up' ? 'text-cool-mint' 
    : trend.direction === 'down' ? 'text-signal-red' 
    : 'text-soft-silver';
  
  const trendBg = trend.direction === 'up' ? 'bg-cool-mint/10' 
    : trend.direction === 'down' ? 'bg-signal-red/10' 
    : 'bg-white/[0.05]';
  
  // Format value display
  const displayValue = () => {
    if (prefix) return `${prefix}${value.toLocaleString()}`;
    if (suffix) return `${value}${suffix}`;
    return value.toLocaleString();
  };

  return (
    <div className="p-5 bg-graphite/50 rounded-xl border border-white/[0.06] hover:border-white/[0.12] transition-colors">
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-lg bg-electric-ice-blue/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-electric-ice-blue" />
        </div>
        <div className={cn("flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium", trendBg, trendColor)}>
          <TrendIcon className="w-3 h-3" />
          <span>{trend.change}%</span>
        </div>
      </div>
      <div className="mt-4">
        <p className="text-2xl font-semibold text-cloud-white">{displayValue()}</p>
        <p className="text-sm text-soft-silver mt-1">{label}</p>
      </div>
    </div>
  );
};

// ==================== CHART TOOLTIP ====================
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-graphite border border-white/[0.1] rounded-lg p-3 shadow-xl">
        <p className="text-cloud-white font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.name.includes('$') || entry.dataKey.includes('collected') || entry.dataKey.includes('outstanding') 
              ? `$${entry.value.toLocaleString()}` 
              : entry.dataKey.includes('percentage') 
                ? `${entry.value}%`
                : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// ==================== EVENT ATTENDANCE CHART ====================
const EventAttendanceChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center text-soft-silver">
        No event data available
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
        <XAxis 
          dataKey="event_name" 
          stroke="#9ca3af" 
          fontSize={12}
          angle={-45}
          textAnchor="end"
          height={80}
        />
        <YAxis 
          stroke="#9ca3af" 
          fontSize={12}
          tickFormatter={(value) => `${value}%`}
          domain={[0, 100]}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar 
          dataKey="attendance_percentage" 
          fill="#38bdf8" 
          radius={[4, 4, 0, 0]}
          name="Attendance %"
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

// ==================== DUES COLLECTION CHART ====================
const DuesCollectionChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center text-soft-silver">
        No dues data available
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
        <XAxis dataKey="month" stroke="#9ca3af" fontSize={12} />
        <YAxis 
          stroke="#9ca3af" 
          fontSize={12}
          tickFormatter={(value) => `$${value}`}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          wrapperStyle={{ color: '#9ca3af' }}
          formatter={(value) => <span className="text-soft-silver">{value}</span>}
        />
        <Line 
          type="monotone" 
          dataKey="collected" 
          stroke="#34d399" 
          strokeWidth={2}
          dot={{ fill: '#34d399', strokeWidth: 2 }}
          name="Collected"
        />
        <Line 
          type="monotone" 
          dataKey="outstanding" 
          stroke="#fbbf24" 
          strokeWidth={2}
          dot={{ fill: '#fbbf24', strokeWidth: 2 }}
          name="Outstanding"
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// ==================== DOCUMENT PROGRESS ====================
const DocumentProgress = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center text-soft-silver py-8">
        No documents to track
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {data.map((item) => (
        <div key={item.doc_type} className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-cloud-white">{item.label}</span>
            <span className="text-soft-silver">
              {item.signed}/{item.required} ({item.completion_rate}%)
            </span>
          </div>
          <Progress 
            value={item.completion_rate} 
            className="h-2 bg-graphite"
          />
        </div>
      ))}
    </div>
  );
};

// ==================== ENGAGEMENT LEADERBOARD ====================
const EngagementLeaderboard = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center text-soft-silver py-8">
        No engagement data available
      </div>
    );
  }

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-5 h-5 text-yellow-400" />;
    if (rank === 2) return <Award className="w-5 h-5 text-gray-300" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 flex items-center justify-center text-soft-silver font-medium">{rank}</span>;
  };

  return (
    <div className="space-y-3">
      {data.map((member) => (
        <div 
          key={member.user_id}
          className={cn(
            "flex items-center justify-between p-3 rounded-lg border transition-colors",
            member.rank <= 3 
              ? "bg-electric-ice-blue/5 border-electric-ice-blue/20" 
              : "bg-obsidian/30 border-white/[0.04]"
          )}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 flex justify-center">
              {getRankIcon(member.rank)}
            </div>
            <div className="w-8 h-8 rounded-full bg-electric-ice-blue/10 flex items-center justify-center overflow-hidden">
              {member.avatar_url ? (
                <img src={member.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <Users className="w-4 h-4 text-electric-ice-blue" />
              )}
            </div>
            <div>
              <p className="text-cloud-white font-medium">{member.name}</p>
              <div className="flex gap-3 text-xs text-soft-silver">
                <span>{member.events_attended} events</span>
                <span>{member.dues_on_time} dues</span>
                <span>{member.docs_signed} docs</span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xl font-semibold text-electric-ice-blue">{member.total_score}</p>
            <p className="text-xs text-soft-silver">points</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// ==================== MAIN ANALYTICS PAGE ====================
export const Analytics = () => {
  const { organization, session, orgRole } = useAuth();
  const { hasOrgRole } = usePermissions();
  
  // Check permissions - must be owner, admin, or event_manager
  const canViewAnalytics = hasOrgRole(['owner', 'admin', 'event_manager']);

  // State
  const [overviewStats, setOverviewStats] = useState(null);
  const [scope, setScope] = useState(null);
  const [eventAttendance, setEventAttendance] = useState([]);
  const [duesCollection, setDuesCollection] = useState([]);
  const [documentProgress, setDocumentProgress] = useState([]);
  const [engagement, setEngagement] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all analytics data based on role scope
  const fetchAnalytics = useCallback(async () => {
    if (!organization?.id || !session?.access_token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // First fetch overview stats to get scope
      const overviewRes = await fetch(
        `${API_URL}/api/analytics/overview/${organization.id}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (!overviewRes.ok) {
        throw new Error('Failed to load analytics data');
      }

      const overviewData = await overviewRes.json();
      const { scope: userScope, role, ...stats } = overviewData;
      setOverviewStats(stats);
      setScope(userScope);

      // Always fetch event attendance (available to all analytics viewers)
      const attendanceRes = await fetch(
        `${API_URL}/api/analytics/event-attendance/${organization.id}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );
      if (attendanceRes.ok) {
        setEventAttendance(await attendanceRes.json());
      }

      // Fetch dues, documents, engagement only if scope allows
      if (userScope?.can_view_dues_chart) {
        const duesRes = await fetch(
          `${API_URL}/api/analytics/dues-collection/${organization.id}`,
          { headers: { Authorization: `Bearer ${session.access_token}` } }
        );
        if (duesRes.ok) {
          setDuesCollection(await duesRes.json());
        }
      }

      if (userScope?.can_view_documents) {
        const docsRes = await fetch(
          `${API_URL}/api/analytics/document-progress/${organization.id}`,
          { headers: { Authorization: `Bearer ${session.access_token}` } }
        );
        if (docsRes.ok) {
          setDocumentProgress(await docsRes.json());
        }
      }

      if (userScope?.can_view_leaderboard) {
        const engagementRes = await fetch(
          `${API_URL}/api/analytics/member-engagement/${organization.id}`,
          { headers: { Authorization: `Bearer ${session.access_token}` } }
        );
        if (engagementRes.ok) {
          setEngagement(await engagementRes.json());
        }
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [organization?.id, session?.access_token]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Access denied
  if (!canViewAnalytics) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <EmptyState
          icon="lock"
          title="Access Restricted"
          description="Analytics requires owner, admin, or event manager role."
        />
      </div>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
          <p className="text-soft-silver text-sm">Loading analytics...</p>
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <EmptyState
          icon="alert-circle"
          title="Error Loading Analytics"
          description={error}
          actionLabel="Retry"
          onAction={fetchAnalytics}
        />
      </div>
    );
  }

  // Map stat icons
  const statIcons = {
    active_members: Users,
    dues_collection_rate: DollarSign,
    event_attendance_rate: Calendar,
    document_signature_rate: FileText,
    rush_conversion_rate: UserPlus,
    wallet_balance: Wallet,
  };

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <PageHeader
        title="Analytics Dashboard"
        subtitle="Organization performance metrics and insights"
      />

      {/* Six Stat Cards */}
      {overviewStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(overviewStats).map(([key, stat]) => {
            // Skip non-stat entries
            if (key === 'scope' || key === 'role' || !stat?.label) return null;
            return (
              <StatCard
                key={key}
                stat={stat}
                icon={statIcons[key] || Users}
              />
            );
          })}
        </div>
      )}

      {/* Charts Row - Event attendance is always visible */}
      <div className={cn(
        "grid gap-6",
        scope?.can_view_dues_chart ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"
      )}>
        {/* Event Attendance Bar Chart - Always visible */}
        <div className="p-5 bg-graphite/50 rounded-xl border border-white/[0.06]">
          <h3 className="text-lg font-medium text-cloud-white mb-4">
            Event Attendance (Last 6 Events)
          </h3>
          <EventAttendanceChart data={eventAttendance} />
        </div>

        {/* Dues Collection Line Chart - Owner/Admin only */}
        {scope?.can_view_dues_chart && (
          <div className="p-5 bg-graphite/50 rounded-xl border border-white/[0.06]">
            <h3 className="text-lg font-medium text-cloud-white mb-4">
              Dues Collection (Last 3 Months)
            </h3>
            <DuesCollectionChart data={duesCollection} />
          </div>
        )}
      </div>

      {/* Document Progress & Leaderboard Row - Owner/Admin only */}
      {(scope?.can_view_documents || scope?.can_view_leaderboard) && (
        <div className={cn(
          "grid gap-6",
          scope?.can_view_documents && scope?.can_view_leaderboard 
            ? "grid-cols-1 lg:grid-cols-2" 
            : "grid-cols-1"
        )}>
          {/* Document Progress by Type - Owner/Admin only */}
          {scope?.can_view_documents && (
            <div className="p-5 bg-graphite/50 rounded-xl border border-white/[0.06]">
              <h3 className="text-lg font-medium text-cloud-white mb-4">
                Document Completion by Type
              </h3>
              <DocumentProgress data={documentProgress} />
            </div>
          )}

          {/* Member Engagement Leaderboard - Owner/Admin only */}
          {scope?.can_view_leaderboard && (
            <div className="p-5 bg-graphite/50 rounded-xl border border-white/[0.06]">
              <h3 className="text-lg font-medium text-cloud-white mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-400" />
                Member Engagement Leaderboard
              </h3>
              <EngagementLeaderboard data={engagement} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Analytics;
